/**
 * Collapsible sidebar navigation — section dividers, dynamic server status.
 * Collapsed state and theme persist to localStorage.
 */

import { routes, icons } from "../../runtime/router/routes.js";
import { bus } from "../../runtime/core/event-bus.js";
import { authFetch } from "../../runtime/auth.js";

const STORAGE_KEY = "cm_sidebar_collapsed";
const THEME_KEY = "cm_theme";

// ─── Theme helpers ───

function applyTheme(theme: string): void {
  if (theme === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

export function initTheme(): void {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved) {
    applyTheme(saved);
  }
}

// ─── Server health probe ───

type ServerState = "connected" | "connecting" | "disconnected";
let _healthTimer: ReturnType<typeof setInterval> | null = null;
let _lastState: ServerState = "connecting";

async function probeServer(sidebar: HTMLElement): Promise<void> {
  const nextState: ServerState = await (authFetch("/stats")
    .then(r => r.ok ? "connected" as const : "disconnected" as const)
    .catch(() => "disconnected" as const));

  if (nextState !== _lastState) {
    _lastState = nextState;
    updateStatusDot(sidebar, nextState);
    bus.emit("server:status", { state: nextState });
  }
}

function startHealthPoll(sidebar: HTMLElement): void {
  // Initial probe
  updateStatusDot(sidebar, "connecting");
  probeServer(sidebar);
  _healthTimer = setInterval(() => {
    if (!sidebar.isConnected) {
      clearInterval(_healthTimer!);
      _healthTimer = null;
      return;
    }
    probeServer(sidebar);
  }, 15000);
}

function updateStatusDot(sidebar: HTMLElement, state: ServerState): void {
  const dot = sidebar.querySelector(".status-dot") as HTMLElement;
  const text = sidebar.querySelector(".sidebar-status-text") as HTMLElement;
  if (!dot) return;

  dot.className = `status-dot status-${state}`;
  if (text) {
    switch (state) {
      case "connected": text.textContent = "Connected"; break;
      case "connecting": text.textContent = "Checking..."; break;
      case "disconnected": text.textContent = "Offline"; break;
    }
  }
}

// ─── Section-aware nav rendering ───

const SECTION_LABELS: Record<string, string> = {
  admin: "Manage",
};

function renderNav(currentPath: string): string {
  let html = "";
  let prevSection: string | undefined;

  for (const r of routes) {
    if (r.section === undefined) continue; // hidden route (e.g. /room/:sessionId)
    // Insert divider when section changes
    if (r.section !== prevSection && prevSection != null) {
      const label = SECTION_LABELS[r.section ?? ""] || "";
      html += `<div class="sidebar-section-divider">${label ? `<span class="sidebar-section-label">${label}</span>` : ""}</div>`;
    }
    prevSection = r.section;
    html += navItem(r, currentPath);
  }

  return html;
}

// ─── Sidebar ───

export function initSidebar(sidebar: HTMLElement, currentPath: string): () => void {
  const collapsed = localStorage.getItem(STORAGE_KEY) === "true";
  if (collapsed) sidebar.classList.add("collapsed");

  render(sidebar, currentPath);
  startHealthPoll(sidebar);

  // Toggle button
  const toggle = sidebar.querySelector(".sidebar-toggle") as HTMLElement;
  toggle?.addEventListener("click", () => {
    sidebar.classList.toggle("collapsed");
    const now = sidebar.classList.contains("collapsed");
    localStorage.setItem(STORAGE_KEY, String(now));
    bus.emit("sidebar:toggle", { collapsed: now });
  });

  // Theme toggle
  const themeBtn = sidebar.querySelector(".theme-toggle") as HTMLElement;
  themeBtn?.addEventListener("click", () => {
    const current = document.documentElement.getAttribute("data-theme");
    const next = current === "dark" ? "light" : "dark";
    applyTheme(next);
    localStorage.setItem(THEME_KEY, next);
  });

  // Listen for route changes to update active state
  const off = bus.on("route:changed", ({ path }) => {
    updateActive(sidebar, path);
  });

  // Also handle direct hash changes (e.g., browser back/forward)
  const onHash = () => updateActive(sidebar, location.hash.slice(1) || "/");
  window.addEventListener("hashchange", onHash);

  return () => {
    off();
    window.removeEventListener("hashchange", onHash);
    if (_healthTimer) { clearInterval(_healthTimer); _healthTimer = null; }
  };
}

function render(sidebar: HTMLElement, currentPath: string): void {
  sidebar.innerHTML = `
    <div class="sidebar-header">
      <span class="sidebar-logo"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg></span>
      <span class="sidebar-title">SecondSee</span>
      <button class="sidebar-toggle" title="Toggle sidebar">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
    </div>
    <nav class="sidebar-nav">
      ${renderNav(currentPath)}
    </nav>
    <div class="sidebar-footer">
      <div class="sidebar-status">
        <span class="status-dot status-connecting"></span>
        <span class="sidebar-status-text">Checking...</span>
      </div>
      <button class="theme-toggle" title="Toggle theme">
        <span class="theme-icon theme-icon-light">&#9788;</span>
        <span class="theme-icon theme-icon-dark">&#9790;</span>
      </button>
    </div>
  `;

  // Bind navigation clicks
  sidebar.querySelectorAll<HTMLAnchorElement>(".sidebar-link").forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const path = link.getAttribute("href")!.slice(1); // remove #
      location.hash = path;
    });
  });

  // Bind ToC clicks
  sidebar.querySelectorAll<HTMLElement>(".sidebar-toc-item").forEach(item => {
    item.addEventListener("click", (e) => {
      e.preventDefault();
      handleTocClick(item);
    });
  });
}

function navItem(route: typeof routes[0], currentPath: string): string {
  const active = isActive(route.path, currentPath);
  let html = `
    <a href="#${route.path}" class="sidebar-link${active ? " active" : ""}" title="${route.title}">
      <span class="sidebar-icon">${icons[route.icon] || ""}</span>
      <span class="sidebar-label">${route.title}</span>
    </a>
  `;

  if (active && route.subsections && route.subsections.length > 0) {
    html += `<div class="sidebar-toc">${route.subsections.map(s =>
      `<a class="sidebar-toc-item" data-toc="${s.id}"${s.click ? ` data-toc-click="${s.click}"` : ""}${s.href ? ` data-toc-href="${s.href}"` : ""} title="${s.label}">${s.label}</a>`
    ).join("")}</div>`;
  }

  return html;
}

function isActive(routePath: string, currentPath: string): boolean {
  if (routePath === "/") return currentPath === "/" || currentPath === "";
  return currentPath.startsWith(routePath);
}

function updateActive(sidebar: HTMLElement, path: string): void {
  sidebar.querySelectorAll(".sidebar-link").forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) return;
    const routePath = href.slice(1); // remove #
    link.classList.toggle("active", isActive(routePath, path));
  });

  // Re-render nav to show/hide subsections for the new active route
  const nav = sidebar.querySelector(".sidebar-nav");
  if (nav) nav.innerHTML = renderNav(path);
  // Re-bind nav link clicks
  sidebar.querySelectorAll<HTMLAnchorElement>(".sidebar-link").forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const p = link.getAttribute("href")!.slice(1);
      location.hash = p;
    });
  });
  // Re-bind ToC clicks
  sidebar.querySelectorAll<HTMLElement>(".sidebar-toc-item").forEach(item => {
    item.addEventListener("click", (e) => {
      e.preventDefault();
      handleTocClick(item);
    });
  });
}

function handleTocClick(item: HTMLElement): void {
  const href = item.dataset.tocHref;
  if (href) {
    location.hash = href;
    return;
  }
  const clickSelector = item.dataset.tocClick;
  if (clickSelector) {
    // Use requestAnimationFrame to ensure the page DOM has settled
    // before dispatching the programmatic click (avoids race with
    // lazy-loaded page modules that set innerHTML + bind events).
    requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>(clickSelector);
      target?.click();
    });
    return;
  }
  const targetId = item.dataset.toc;
  if (!targetId) return;
  const el = document.getElementById(targetId);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}
